import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Video,
  CheckCircle,
  XCircle,
  BookOpen,
  Youtube,
  Target,
  ClipboardList,
  Calendar,
  Mail,
  ExternalLink,
  Loader2,
} from 'lucide-react';
import { useAuth } from '@core/auth/useAuth';
import type { ApplicationStatus } from '@core/enums';
import { candidateService } from '@core/supabase/services/candidateService';
import { jobService } from '@core/supabase/services/jobService';
import { isApplicationStatusConflictError } from '@core/supabase/applicationStatusConflict';
import { applicationService } from '@core/supabase/services/applicationService';
import { optimizationService, type B2BOptimization } from '@core/supabase/services/optimizationService';
import {
  interviewEventService,
  type InterviewEvent,
  type SchedulingProvider,
} from '@core/supabase/services/interviewEventService';
import {
  scorecardService,
  type InterviewScorecard,
  type ScorecardRecommendation,
  RECOMMENDATION_LABELS,
} from '@core/supabase/services/scorecardService';
import { PageHeader } from '@shared/components/ui/PageHeader';
import { formatDateTimeGmt } from '@shared/lib/formatDateTimeGmt';
import { ListOperationsDropdown } from '@shared/components/ui/ListOperationsDropdown';
import { objectHistoryUrl } from '@shared/lib/objectHistoryUrl';
import { listExportNotImplemented } from '@shared/lib/listExportStub';
import { FullPageSpinner } from '@shared/components/ui/Spinner';
import { Candidate, Application } from '@core/types';
import { Job } from '@core/types';

function formatInterviewWhen(iso: string) {
  const s = formatDateTimeGmt(iso);
  return s === '—' ? iso : s;
}

function sortInterviewEventsDesc(list: InterviewEvent[]) {
  return [...list].sort((a, b) => {
    const ta = a.scheduled_at ?? a.created_at;
    const tb = b.scheduled_at ?? b.created_at;
    return tb.localeCompare(ta);
  });
}

function formatEvWhen(ev: InterviewEvent): string {
  if (!ev.scheduled_at) return 'Slot TBD';
  return formatInterviewWhen(ev.scheduled_at);
}

function pipelineForScheduledRound(round: number): ApplicationStatus {
  return round >= 2 ? 'interview_2_scheduled' : 'interview_1_scheduled';
}

function pipelineForConductedRound(round: number): ApplicationStatus {
  return round >= 2 ? 'interview_2_conducted' : 'interview_1_conducted';
}

function lifecycleRibbon(ev: InterviewEvent): string {
  if (ev.status === 'conducted' || ev.status === 'cancelled' || ev.status === 'no_show') return 'Closed';
  if (ev.status === 'scheduled') return 'On calendar';
  if (ev.invite_sent_at) return 'Invite sent';
  if (ev.status === 'awaiting_booking') return 'Self-book link';
  return 'Draft';
}

function bookingInviteHtml(url: string, label: string): string {
  const safe = url.replace(/"/g, '&quot;');
  return `<p style="margin:0 0 12px;color:#e4e4e7;font-size:14px;line-height:1.6;">Please use this link to choose a time: <a href="${safe}" style="color:#38bdf8;">${label}</a></p>`;
}

function GapAnalysisBlock({ data }: { data: unknown }) {
  if (data == null) return null;
  if (Array.isArray(data) && data.length === 0) return null;
  if (Array.isArray(data) && data.length > 0) {
    return (
      <ul className="space-y-2 ggj-text-body vscode-text-secondary">
        {data.map((row, i) => {
          const o = row as Record<string, unknown>;
          const skill = typeof o.skill === 'string' ? o.skill : typeof o.name === 'string' ? o.name : null;
          const severity = typeof o.severity === 'string' ? o.severity : null;
          const suggestion = typeof o.suggestion === 'string' ? o.suggestion : null;
          if (!skill && !suggestion) {
            return (
              <li key={i} className="vscode-text-tertiary">
                <pre className="text-[11px] whitespace-pre-wrap break-words">{JSON.stringify(row, null, 2)}</pre>
              </li>
            );
          }
          return (
            <li key={i} className="border vscode-border rounded-[10px] p-2 bg-gray-900/40">
              {skill && <span className="font-medium vscode-text-primary">{skill}</span>}
              {severity && <span className="ggj-text-meta text-amber-400 ml-2">({severity})</span>}
              {suggestion && <p className="ggj-text-meta vscode-text-secondary mt-1">{suggestion}</p>}
            </li>
          );
        })}
      </ul>
    );
  }
  return (
    <details className="ggj-text-meta vscode-text-secondary">
      <summary className="cursor-pointer hover:vscode-text-primary">View structured data</summary>
      <pre className="mt-2 p-2 rounded bg-gray-900 overflow-x-auto text-[11px] max-h-48 overflow-y-auto">
        {typeof data === 'string' ? data : JSON.stringify(data, null, 2)}
      </pre>
    </details>
  );
}

// ── §28.D InterviewNotesGrid ────────────────────────────────────────────────

interface InterviewNote {
  id: string;
  title: string;
  body: string;
  createdBy: string;
  avatarInitial: string;
}

const STUB_NOTES: InterviewNote[] = [];

function InterviewNotesGrid() {
  const [notes, setNotes] = useState<InterviewNote[]>(STUB_NOTES);

  const handleNewNote = () => {
    setNotes((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        title: 'AS Mobbin <> New Candidate',
        body: 'Interview notes will appear here once the note is saved. Add key observations, highlights, and concerns from the conversation.',
        createdBy: 'You',
        avatarInitial: 'Y',
      },
    ]);
  };

  if (notes.length === 0) {
    return (
      <div className="px-6 py-8">
        <div
          className="flex h-40 w-full max-w-xs cursor-pointer flex-col items-center justify-center gap-2 rounded-[10px] border-2 border-dashed text-[13px] transition-colors hover:bg-[var(--vscode-bg-hover)]"
          style={{ borderColor: 'var(--vscode-border-primary)', color: 'var(--vscode-text-tertiary)' }}
          role="button"
          tabIndex={0}
          onClick={handleNewNote}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleNewNote(); }}
          aria-label="Add new note"
        >
          <span className="ggj-text-lead">+</span>
          <span>New note</span>
        </div>
      </div>
    );
  }

  return (
    <div className="px-6 py-6">
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {notes.map((note) => (
          <div
            key={note.id}
            className="flex flex-col rounded-[10px] border"
            style={{
              background: 'var(--vscode-bg-elevated)',
              borderColor: 'var(--vscode-border-primary)',
              overflow: 'hidden',
            }}
          >
            {/* Card header */}
            <div className="flex items-center gap-2 border-b px-4 py-2.5" style={{ borderColor: 'var(--vscode-border-primary)' }}>
              <span className="flex-1 truncate text-[13px] font-semibold" style={{ color: 'var(--vscode-text-primary)' }}>
                {note.title}
              </span>
              <button
                type="button"
                aria-label="Note options"
                className="rounded p-1 hover:bg-[var(--vscode-bg-hover)]"
                style={{ color: 'var(--vscode-text-tertiary)' }}
              >
                ⋯
              </button>
            </div>

            {/* Body preview — 6-line fade */}
            <div
              className="relative flex-1 px-4 py-3"
              style={{
                maxHeight: 120,
                overflow: 'hidden',
                maskImage: 'linear-gradient(to bottom, black 60%, transparent)',
                WebkitMaskImage: 'linear-gradient(to bottom, black 60%, transparent)',
              }}
            >
              <p className="text-[13px] leading-[1.55]" style={{ color: 'var(--vscode-text-secondary)' }}>
                {note.body}
              </p>
            </div>

            {/* Footer */}
            <div className="flex items-center gap-2 border-t px-4 py-2.5" style={{ borderColor: 'var(--vscode-border-primary)' }}>
              <span
                className="flex h-4 w-4 items-center justify-center rounded-full bg-emerald-500/20 text-[10px] font-bold text-emerald-700"
                aria-hidden
              >
                {note.avatarInitial}
              </span>
              <span className="text-[12px]" style={{ color: 'var(--vscode-text-tertiary)' }}>
                Created by {note.createdBy}
              </span>
            </div>
          </div>
        ))}

        {/* Always show a "new note" dashed card at the end */}
        <div
          className="flex h-40 cursor-pointer flex-col items-center justify-center gap-2 rounded-[10px] border-2 border-dashed text-[13px] transition-colors hover:bg-[var(--vscode-bg-hover)]"
          style={{ borderColor: 'var(--vscode-border-primary)', color: 'var(--vscode-text-tertiary)' }}
          role="button"
          tabIndex={0}
          onClick={handleNewNote}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleNewNote(); }}
          aria-label="Add new note"
        >
          <span className="ggj-text-lead">+</span>
          <span>New note</span>
        </div>
      </div>
    </div>
  );
}

// ── InterviewsPage ───────────────────────────────────────────────────────────

export function InterviewsPage() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = (searchParams.get('tab') as 'interviews' | 'notes') ?? 'interviews';
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [selectedCandidate, setSelectedCandidate] = useState<string>('');
  const [selectedJob, setSelectedJob] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [pairedApp, setPairedApp] = useState<Application | null>(null);

  const [detailLoading, setDetailLoading] = useState(false);
  const [interviewEvents, setInterviewEvents] = useState<InterviewEvent[]>([]);
  const [scorecards, setScorecards] = useState<InterviewScorecard[]>([]);
  const [optimization, setOptimization] = useState<B2BOptimization | null>(null);

  const [formEventId, setFormEventId] = useState<string>('');
  const [formRound, setFormRound] = useState('general');
  const [formOverall, setFormOverall] = useState<number>(3);
  const [formRec, setFormRec] = useState<ScorecardRecommendation>('maybe');
  const [formNotes, setFormNotes] = useState('');
  const [rubricRows, setRubricRows] = useState<{ criterion: string; score: number }[]>([
    { criterion: '', score: 3 },
    { criterion: '', score: 3 },
    { criterion: '', score: 3 },
  ]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [lcRound, setLcRound] = useState('1');
  const [lcProvider, setLcProvider] = useState<SchedulingProvider | ''>('');
  const [lcBookingUrl, setLcBookingUrl] = useState('');
  const [lcNotes, setLcNotes] = useState('');
  const [lcCreating, setLcCreating] = useState(false);
  const [lcErr, setLcErr] = useState<string | null>(null);
  const [lcBusyId, setLcBusyId] = useState<string | null>(null);
  const [confirmForId, setConfirmForId] = useState<string | null>(null);
  const [confirmWhen, setConfirmWhen] = useState('');
  const [confirmLocation, setConfirmLocation] = useState('');
  const [confirmDuration, setConfirmDuration] = useState('60');
  const [confirmNotify, setConfirmNotify] = useState(true);
  const [draftUrlInput, setDraftUrlInput] = useState('');

  useEffect(() => {
    if (!user?.orgId) return;
    setLoading(true);
    Promise.all([
      candidateService.list(user.orgId),
      jobService.list(user.orgId, { status: 'open' }),
    ])
      .then(([c, j]) => {
        setCandidates(c);
        setJobs(j);
      })
      .finally(() => setLoading(false));
  }, [user?.orgId]);

  useEffect(() => {
    const c = searchParams.get('candidateId');
    const j = searchParams.get('jobId');
    if (c && candidates.some((x) => x.id === c)) setSelectedCandidate(c);
    if (j && jobs.some((x) => x.id === j)) setSelectedJob(j);
  }, [searchParams, candidates, jobs]);

  useEffect(() => {
    if (!user?.orgId || !selectedCandidate || !selectedJob) {
      setPairedApp(null);
      return;
    }
    applicationService.list(user.orgId, { candidateId: selectedCandidate, jobId: selectedJob }).then((list) => {
      const apps = Array.isArray(list) ? list : [];
      setPairedApp(apps[0] ?? null);
    });
  }, [user?.orgId, selectedCandidate, selectedJob]);

  useEffect(() => {
    if (!user?.orgId || !pairedApp) {
      setInterviewEvents([]);
      setScorecards([]);
      setOptimization(null);
      return;
    }
    const orgId = user.orgId;
    const appId = pairedApp.id;
    setDetailLoading(true);
    setSaveError(null);
    Promise.all([
      interviewEventService.listByApplication(appId),
      scorecardService.listByApplication(appId),
      optimizationService.getByApplication(appId),
    ])
      .then(([ev, sc, opt]) => {
        setInterviewEvents(sortInterviewEventsDesc(ev));
        const maxR = ev.reduce((m, e) => Math.max(m, e.round), 0);
        setLcRound(String(maxR + 1 || 1));
        setLcBookingUrl('');
        setLcNotes('');
        setLcProvider('');
        setLcErr(null);
        setConfirmForId(null);
        setDraftUrlInput('');
        setScorecards(sc);
        setOptimization(opt);
        setFormEventId('');
        setFormRound('general');
        setFormOverall(3);
        setFormRec('maybe');
        setFormNotes('');
        setRubricRows([
          { criterion: '', score: 3 },
          { criterion: '', score: 3 },
          { criterion: '', score: 3 },
        ]);
      })
      .finally(() => setDetailLoading(false));
  }, [user?.orgId, pairedApp?.id]);

  const onEventPick = (eventId: string) => {
    setFormEventId(eventId);
    if (!eventId) {
      setFormRound('general');
      return;
    }
    const ev = interviewEvents.find((e) => e.id === eventId);
    if (ev) setFormRound(String(ev.round));
  };

  const submitScorecard = async () => {
    if (!user?.orgId || !pairedApp) return;
    setSaving(true);
    setSaveError(null);
    const rubric = rubricRows
      .filter((r) => r.criterion.trim())
      .map((r) => ({ criterion: r.criterion.trim(), score: r.score, note: '' }));
    try {
      await scorecardService.create({
        org_id: user.orgId,
        application_id: pairedApp.id,
        interview_event_id: formEventId || null,
        interviewer_id: user.orgUserId ?? null,
        interviewer_name: user.name?.trim() || user.email || null,
        round: formRound.trim() || 'general',
        overall_score: formOverall,
        recommendation: formRec,
        rubric,
        notes: formNotes.trim() || null,
      });
      const next = await scorecardService.listByApplication(pairedApp.id);
      setScorecards(next);
      setFormNotes('');
      setRubricRows([
        { criterion: '', score: 3 },
        { criterion: '', score: 3 },
        { criterion: '', score: 3 },
      ]);
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : 'Could not save scorecard');
    } finally {
      setSaving(false);
    }
  };

  const cand = candidates.find((c) => c.id === selectedCandidate);
  const job = jobs.find((j) => j.id === selectedJob);

  const interviewsReturnPath =
    selectedCandidate && selectedJob
      ? `/interviews/prep?candidateId=${encodeURIComponent(selectedCandidate)}&jobId=${encodeURIComponent(selectedJob)}`
      : '/interviews/prep';

  const candidateSkills = new Set((cand?.parsed_skills ?? []).map((s) => s.toLowerCase()));
  const jobSkills = (job?.skills_required ?? []).map((s) => s.toLowerCase());
  const matchedSkills = jobSkills.filter((s) => candidateSkills.has(s));
  const missingSkills = jobSkills.filter((s) => !candidateSkills.has(s));

  const matFromOptimization =
    optimization != null &&
    (optimization.gap_analysis != null || optimization.fitment_analysis != null);

  return (
    <div>
      <PageHeader
        icon={Video}
        breadcrumb={[{ label: 'Pipeline', href: '/interviews' }, { label: 'Interview prep' }]}
        title="Interview prep & scorecards"
        subtitle="Select candidate + role → skill gaps, MAT / fitment, lifecycle actions. Schedule overview: Interview hub (sidebar)."
        breadcrumbRowEnd={
          <ListOperationsDropdown
            objectHistoryHref={objectHistoryUrl('interview_prep')}
            onExport={(fmt, scope) => listExportNotImplemented('Interview prep', fmt, scope)}
          />
        }
      />

      {/* §28.D — Tab row: Interviews | Notes */}
      <div
        className="flex items-center gap-1 border-b px-4 pt-1"
        style={{ borderColor: 'var(--vscode-border-primary)' }}
        role="tablist"
        aria-label="Interviews views"
      >
        {(['interviews', 'notes'] as const).map((tab) => (
          <button
            key={tab}
            type="button"
            role="tab"
            aria-selected={activeTab === tab}
            onClick={() => {
              if (tab === 'interviews') {
                setSearchParams((prev) => {
                  const next = new URLSearchParams(prev);
                  next.delete('tab');
                  return next;
                });
              } else {
                setSearchParams((prev) => {
                  const next = new URLSearchParams(prev);
                  next.set('tab', tab);
                  return next;
                });
              }
            }}
            className="h-9 border-b-2 px-4 text-[13px] font-medium capitalize transition-colors"
            style={{
              borderColor: activeTab === tab ? '#10B981' : 'transparent',
              color: activeTab === tab ? '#10B981' : 'var(--vscode-text-secondary)',
            }}
          >
            {tab === 'interviews' ? 'Interviews' : 'Notes'}
          </button>
        ))}
      </div>

      {activeTab === 'notes' ? (
        <InterviewNotesGrid />
      ) : loading ? (
        <FullPageSpinner />
      ) : (
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-4">
            <div className="card">
              <h2 className="ggj-text-body font-semibold vscode-text-primary mb-3">Select Candidate & Role</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block ggj-text-meta vscode-text-tertiary mb-1">Candidate</label>
                  <select
                    className="input w-full"
                    value={selectedCandidate}
                    onChange={(e) => setSelectedCandidate(e.target.value)}
                  >
                    <option value="">Select candidate</option>
                    {candidates.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.full_name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block ggj-text-meta vscode-text-tertiary mb-1">Job / Role</label>
                  <select className="input w-full" value={selectedJob} onChange={(e) => setSelectedJob(e.target.value)}>
                    <option value="">Select job</option>
                    {jobs.map((j) => (
                      <option key={j.id} value={j.id}>
                        {j.title}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {selectedCandidate && selectedJob && (
              <div className="card">
                <h2 className="ggj-text-body font-semibold vscode-text-primary mb-4 flex items-center gap-2">
                  <Video className="w-4 h-4 text-brand-400" /> Skill Gap Analysis
                </h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <p className="ggj-text-meta vscode-text-tertiary mb-2 flex items-center gap-1">
                      <CheckCircle className="w-4 h-4 text-green-500" /> Matched skills ({matchedSkills.length})
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {matchedSkills.length === 0 ? (
                        <span className="ggj-text-meta vscode-text-tertiary">None</span>
                      ) : (
                        matchedSkills.map((s) => (
                          <span key={s} className="px-2 py-0.5 rounded bg-green-900/50 text-green-300 ggj-text-meta">
                            {s}
                          </span>
                        ))
                      )}
                    </div>
                  </div>
                  <div>
                    <p className="ggj-text-meta vscode-text-tertiary mb-2 flex items-center gap-1">
                      <XCircle className="w-4 h-4 text-red-500" /> Missing skills ({missingSkills.length})
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {missingSkills.length === 0 ? (
                        <span className="ggj-text-meta vscode-text-tertiary">All required skills matched</span>
                      ) : (
                        missingSkills.map((s) => (
                          <span key={s} className="px-2 py-0.5 rounded bg-red-900/50 text-red-300 ggj-text-meta">
                            {s}
                          </span>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {pairedApp && (pairedApp.fitment_score != null || pairedApp.fitment_json) && (
              <div className="card">
                <h2 className="ggj-text-body font-semibold vscode-text-primary mb-3 flex items-center gap-2">
                  <Target className="w-4 h-4 text-brand-400" /> Persisted fitment (application)
                </h2>
                {pairedApp.fitment_score != null && (
                  <div className="flex items-center gap-3 mb-3">
                    <div className="ggj-text-hero font-bold vscode-text-primary">{pairedApp.fitment_score}</div>
                    <div>
                      <div className="ggj-text-meta vscode-text-tertiary">Role-fit score</div>
                      <div className="w-32 h-2 rounded-full bg-gray-700 overflow-hidden mt-1">
                        <div
                          className="h-full rounded-full bg-brand-500"
                          style={{ width: `${Math.min(100, Number(pairedApp.fitment_score))}%` }}
                        />
                      </div>
                    </div>
                  </div>
                )}
                {pairedApp.fitment_json && (
                  <details className="ggj-text-meta vscode-text-secondary">
                    <summary className="cursor-pointer hover:vscode-text-primary">View analysis detail</summary>
                    <pre className="mt-2 p-2 rounded bg-gray-900 overflow-x-auto text-[11px]">
                      {JSON.stringify(pairedApp.fitment_json, null, 2)}
                    </pre>
                  </details>
                )}
                <Link to={`/applications/${pairedApp.id}`} className="ggj-text-meta text-brand-400 hover:underline mt-2 inline-block">
                  Open application →
                </Link>
              </div>
            )}

            {pairedApp && matFromOptimization && (
              <div className="card">
                <h2 className="ggj-text-body font-semibold vscode-text-primary mb-3 flex items-center gap-2">
                  <Target className="w-4 h-4 text-emerald-400" /> Persisted MAT (resume optimize)
                </h2>
                <p className="ggj-text-meta vscode-text-tertiary mb-3">
                  Gap / fitment blobs saved on the optimization row for this application (ACT-020).
                </p>
                {optimization?.gap_analysis != null && (
                  <div className="mb-4">
                    <p className="ggj-text-meta font-medium vscode-text-secondary mb-2">Gap analysis</p>
                    <GapAnalysisBlock data={optimization.gap_analysis} />
                  </div>
                )}
                {optimization?.fitment_analysis != null && (
                  <div>
                    <p className="ggj-text-meta font-medium vscode-text-secondary mb-2">Fitment analysis</p>
                    <GapAnalysisBlock data={optimization.fitment_analysis} />
                  </div>
                )}
                <Link to={`/applications/${pairedApp.id}`} className="ggj-text-meta text-brand-400 hover:underline mt-3 inline-block">
                  Application detail →
                </Link>
              </div>
            )}

            {pairedApp && (
              <div className="card">
                <h2 className="ggj-text-body font-semibold vscode-text-primary mb-3 flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-brand-400" /> Interview lifecycle (§9.8)
                </h2>
                <p className="ggj-text-meta vscode-text-tertiary mb-4">
                  Draft → paste a Luma / Calendly (or any) booking link → email candidate and client → log invite →
                  confirm the booked slot → mark outcome. Confirming a slot or marking conducted updates pipeline
                  stages <span className="vscode-text-secondary">interview_*_scheduled / conducted</span>.
                </p>
                {detailLoading ? (
                  <p className="ggj-text-meta vscode-text-tertiary">Loading schedule…</p>
                ) : (
                  <>
                    <div className="border vscode-border rounded-[10px] p-4 bg-gray-900/30 mb-4 space-y-3">
                      <p className="ggj-text-meta font-medium vscode-text-secondary">Start a new round</p>
                      <div className="grid sm:grid-cols-2 gap-3">
                        <div>
                          <label className="block ggj-text-meta vscode-text-tertiary mb-1">Round</label>
                          <input
                            type="number"
                            min={1}
                            className="input w-full ggj-text-body"
                            value={lcRound}
                            onChange={(e) => setLcRound(e.target.value)}
                          />
                        </div>
                        <div>
                          <label className="block ggj-text-meta vscode-text-tertiary mb-1">Scheduling tool</label>
                          <select
                            className="input w-full ggj-text-body"
                            value={lcProvider}
                            onChange={(e) => setLcProvider(e.target.value as SchedulingProvider | '')}
                          >
                            <option value="">Not specified</option>
                            <option value="luma">Luma</option>
                            <option value="calendly">Calendly</option>
                            <option value="manual">Manual / other link</option>
                          </select>
                        </div>
                        <div className="sm:col-span-2">
                          <label className="block ggj-text-meta vscode-text-tertiary mb-1">Booking page URL (optional)</label>
                          <input
                            className="input w-full ggj-text-body"
                            placeholder="https://…"
                            value={lcBookingUrl}
                            onChange={(e) => setLcBookingUrl(e.target.value)}
                          />
                        </div>
                        <div className="sm:col-span-2">
                          <label className="block ggj-text-meta vscode-text-tertiary mb-1">Internal notes</label>
                          <textarea
                            className="input w-full ggj-text-body min-h-[52px]"
                            value={lcNotes}
                            onChange={(e) => setLcNotes(e.target.value)}
                          />
                        </div>
                      </div>
                      {lcErr && <p className="ggj-text-meta text-red-400">{lcErr}</p>}
                      <button
                        type="button"
                        className="btn-primary ggj-text-body"
                        disabled={lcCreating || !user?.orgId}
                        onClick={async () => {
                          if (!user?.orgId || !pairedApp) return;
                          const r = Number(lcRound);
                          if (!Number.isFinite(r) || r < 1) {
                            setLcErr('Round must be at least 1');
                            return;
                          }
                          setLcCreating(true);
                          setLcErr(null);
                          try {
                            const created = await interviewEventService.createLifecycleEntry({
                              org_id: user.orgId,
                              application_id: pairedApp.id,
                              round: r,
                              scheduling_provider: lcProvider || null,
                              scheduling_booking_url: lcBookingUrl.trim() || null,
                              notes: lcNotes.trim() || null,
                            });
                            setInterviewEvents((prev) => sortInterviewEventsDesc([...prev, created]));
                            const nextMax = [...interviewEvents, created].reduce((m, e) => Math.max(m, e.round), 0);
                            setLcRound(String(nextMax + 1));
                            setLcBookingUrl('');
                            setLcNotes('');
                          } catch (e) {
                            setLcErr(e instanceof Error ? e.message : 'Could not create interview');
                          } finally {
                            setLcCreating(false);
                          }
                        }}
                      >
                        {lcCreating ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Creating…
                          </>
                        ) : (
                          '[Create draft / awaiting-booking row]'
                        )}
                      </button>
                    </div>

                    {interviewEvents.length === 0 ? (
                      <p className="ggj-text-meta vscode-text-tertiary">
                        No interview rows yet. Create one above, or schedule with a fixed time from the application
                        detail.
                      </p>
                    ) : (
                      <ul className="space-y-4">
                        {interviewEvents.map((ev) => {
                          const busy = lcBusyId === ev.id;
                          const isPreSchedule =
                            ev.status === 'draft' || ev.status === 'awaiting_booking';
                          const bookingUrl = ev.scheduling_booking_url?.trim() ?? '';
                          const providerLabel =
                            ev.scheduling_provider === 'luma'
                              ? 'Luma'
                              : ev.scheduling_provider === 'calendly'
                                ? 'Calendly'
                                : ev.scheduling_provider === 'manual'
                                  ? 'Booking link'
                                  : 'Open link';

                          const composeBase = `/emails/compose?applicationId=${pairedApp.id}`;
                          const composeStateCandidate = {
                            returnTo: interviewsReturnPath,
                            recipientOverride: {
                              email: cand?.email ?? '',
                              name: cand?.full_name ?? '',
                            },
                            templateSelect: 'b:interview_confirmation',
                            initialCustomBody: bookingUrl
                              ? bookingInviteHtml(bookingUrl, providerLabel)
                              : '<p style="color:#a1a1aa;font-size:13px;">Paste your scheduling link or instructions in the editor below.</p>',
                          };
                          const clientEmail = pairedApp.client?.contact_email?.trim();
                          const composeStateClient = clientEmail
                            ? {
                                returnTo: interviewsReturnPath,
                                recipientOverride: {
                                  email: clientEmail,
                                  name: pairedApp.client?.contact_name ?? pairedApp.client?.name ?? '',
                                },
                                templateSelect: 'b:first_submission',
                                initialCustomBody: `<p style="color:#e4e4e7;font-size:14px;line-height:1.6;">Interview coordination for <strong>${cand?.full_name ?? 'candidate'}</strong> — round ${ev.round}. ${
                                  bookingUrl
                                    ? `Scheduling page: <a href="${bookingUrl.replace(/"/g, '&quot;')}" style="color:#38bdf8;">${providerLabel}</a>`
                                    : 'We will share the confirmed time shortly.'
                                }</p>`,
                              }
                            : null;

                          return (
                            <li
                              key={ev.id}
                              className="border vscode-border rounded-[10px] p-4 bg-gray-900/20 ggj-text-body"
                            >
                              <div className="flex flex-wrap items-center gap-2 mb-2">
                                <span className="font-medium vscode-text-primary">Round {ev.round}</span>
                                <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded bg-gray-800 vscode-text-secondary">
                                  {lifecycleRibbon(ev)}
                                </span>
                                <span className="ggj-text-meta vscode-text-tertiary">{ev.status}</span>
                              </div>
                              <p className="ggj-text-meta vscode-text-secondary mb-2">
                                {formatEvWhen(ev)}
                                {ev.duration_minutes != null && ev.scheduled_at
                                  ? ` · ${ev.duration_minutes} min`
                                  : ''}
                              </p>
                              {bookingUrl && (
                                <a
                                  href={bookingUrl}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="ggj-text-meta text-brand-400 hover:underline inline-flex items-center gap-1 mb-2"
                                >
                                  <ExternalLink className="w-3 h-3" /> {providerLabel}
                                </a>
                              )}
                              {ev.invite_sent_at && (
                                <p className="ggj-text-meta vscode-text-tertiary mb-2">
                                  Invite logged {formatDateTimeGmt(ev.invite_sent_at)}
                                </p>
                              )}
                              {ev.location && (
                                <p className="ggj-text-meta vscode-text-secondary mb-2 whitespace-pre-wrap">{ev.location}</p>
                              )}

                              {ev.status === 'draft' && !bookingUrl && (
                                <div className="mt-2 space-y-2 border-t vscode-border pt-3">
                                  <p className="ggj-text-meta vscode-text-tertiary">
                                    Paste a booking URL to move this row to &quot;awaiting booking&quot;.
                                  </p>
                                  <div className="flex flex-wrap gap-2 items-end">
                                    <input
                                      className="input flex-1 min-w-[200px] ggj-text-meta"
                                      placeholder="https://calendly.com/… or lu.ma/…"
                                      value={draftUrlInput}
                                      onChange={(e) => setDraftUrlInput(e.target.value)}
                                    />
                                    <button
                                      type="button"
                                      className="btn-secondary ggj-text-meta py-1.5"
                                      disabled={busy || !draftUrlInput.trim()}
                                      onClick={async () => {
                                        setLcBusyId(ev.id);
                                        try {
                                          const u = draftUrlInput.trim();
                                          await interviewEventService.update(ev.id, {
                                            scheduling_booking_url: u,
                                            status: 'awaiting_booking',
                                            scheduling_provider: ev.scheduling_provider ?? 'manual',
                                          });
                                          setInterviewEvents((prev) =>
                                            sortInterviewEventsDesc(
                                              prev.map((e) =>
                                                e.id === ev.id
                                                  ? {
                                                      ...e,
                                                      scheduling_booking_url: u,
                                                      status: 'awaiting_booking',
                                                      scheduling_provider: e.scheduling_provider ?? 'manual',
                                                    }
                                                  : e
                                              )
                                            )
                                          );
                                          setDraftUrlInput('');
                                        } finally {
                                          setLcBusyId(null);
                                        }
                                      }}
                                    >
                                      Save link
                                    </button>
                                  </div>
                                </div>
                              )}

                              {isPreSchedule && (
                                <div className="mt-2 flex flex-wrap gap-2 border-t vscode-border pt-3">
                                  {cand?.email ? (
                                    <Link
                                      to={composeBase}
                                      state={composeStateCandidate}
                                      className="btn-secondary ggj-text-meta py-1.5 inline-flex items-center gap-1"
                                    >
                                      <Mail className="w-3.5 h-3.5" /> [Email candidate]
                                    </Link>
                                  ) : (
                                    <span className="ggj-text-meta text-amber-400">Candidate email missing</span>
                                  )}
                                  {composeStateClient ? (
                                    <Link
                                      to={composeBase}
                                      state={composeStateClient}
                                      className="btn-secondary ggj-text-meta py-1.5 inline-flex items-center gap-1"
                                    >
                                      <Mail className="w-3.5 h-3.5" /> [Email client]
                                    </Link>
                                  ) : (
                                    <span className="ggj-text-meta vscode-text-tertiary">No client contact email</span>
                                  )}
                                  <button
                                    type="button"
                                    className="btn-secondary ggj-text-meta py-1.5"
                                    disabled={busy}
                                    onClick={async () => {
                                      setLcBusyId(ev.id);
                                      try {
                                        const ts = new Date().toISOString();
                                        await interviewEventService.update(ev.id, { invite_sent_at: ts });
                                        setInterviewEvents((prev) =>
                                          prev.map((e) => (e.id === ev.id ? { ...e, invite_sent_at: ts } : e))
                                        );
                                      } finally {
                                        setLcBusyId(null);
                                      }
                                    }}
                                  >
                                    [Mark invite sent]
                                  </button>
                                  <button
                                    type="button"
                                    className="btn-primary ggj-text-meta py-1.5"
                                    disabled={busy}
                                    onClick={() => {
                                      setConfirmForId(ev.id === confirmForId ? null : ev.id);
                                      setConfirmLocation(bookingUrl || ev.location || '');
                                      setConfirmWhen('');
                                    }}
                                  >
                                    {confirmForId === ev.id ? '[Hide slot form]' : '[Confirm booked slot]'}
                                  </button>
                                </div>
                              )}

                              {confirmForId === ev.id && isPreSchedule && (
                                <div className="mt-3 p-3 rounded-[10px] bg-gray-900/50 border vscode-border space-y-2">
                                  <p className="ggj-text-meta vscode-text-tertiary">
                                    Enter the confirmed start time and meeting location (after self-book or internal
                                    coordination).
                                  </p>
                                  <div className="grid sm:grid-cols-2 gap-2">
                                    <div>
                                      <label className="block ggj-text-meta vscode-text-tertiary mb-1">Start *</label>
                                      <input
                                        type="datetime-local"
                                        className="input w-full ggj-text-meta"
                                        value={confirmWhen}
                                        onChange={(e) => setConfirmWhen(e.target.value)}
                                      />
                                    </div>
                                    <div>
                                      <label className="block ggj-text-meta vscode-text-tertiary mb-1">Duration (min)</label>
                                      <input
                                        type="number"
                                        min={15}
                                        step={5}
                                        className="input w-full ggj-text-meta"
                                        value={confirmDuration}
                                        onChange={(e) => setConfirmDuration(e.target.value)}
                                      />
                                    </div>
                                    <div className="sm:col-span-2">
                                      <label className="block ggj-text-meta vscode-text-tertiary mb-1">
                                        Location / video link *
                                      </label>
                                      <input
                                        className="input w-full ggj-text-meta"
                                        value={confirmLocation}
                                        onChange={(e) => setConfirmLocation(e.target.value)}
                                        placeholder="Meet / Zoom / office"
                                      />
                                    </div>
                                  </div>
                                  <label className="flex items-center gap-2 ggj-text-meta vscode-text-secondary">
                                    <input
                                      type="checkbox"
                                      checked={confirmNotify}
                                      onChange={(e) => setConfirmNotify(e.target.checked)}
                                      className="rounded border-gray-600"
                                    />
                                    Queue candidate + panel emails (send queue)
                                  </label>
                                  <button
                                    type="button"
                                    className="btn-primary ggj-text-meta py-1.5"
                                    disabled={busy || !confirmWhen.trim() || !confirmLocation.trim()}
                                    onClick={async () => {
                                      if (!user?.orgId || !pairedApp || !confirmWhen) return;
                                      const iso = new Date(confirmWhen).toISOString();
                                      const dur = Number(confirmDuration);
                                      setLcBusyId(ev.id);
                                      try {
                                        await interviewEventService.update(ev.id, {
                                          scheduled_at: iso,
                                          status: 'scheduled',
                                          location: confirmLocation.trim(),
                                          duration_minutes: Number.isFinite(dur) ? dur : 60,
                                        });
                                        if (confirmNotify) {
                                          await interviewEventService.enqueueInterviewConfirmationEmail({
                                            org_id: user.orgId,
                                            application_id: pairedApp.id,
                                            scheduled_at: iso,
                                            round: ev.round,
                                            location: confirmLocation.trim(),
                                            candidateName: cand?.full_name,
                                            candidateEmail: cand?.email ?? null,
                                            jobTitle: pairedApp.job?.title ?? job?.title,
                                            clientName: pairedApp.client?.name,
                                            recruiterName: user.name,
                                            orgName: user.orgName ?? undefined,
                                            interviewerEmail: ev.interviewer_email,
                                            interviewerName: ev.interviewer_name,
                                            panelRecipients: (ev.attendees ?? [])
                                              .filter((a) => a.email?.trim())
                                              .map((a) => ({ email: a.email!.trim(), name: a.name })),
                                            createdBy: user.name,
                                          });
                                        }
                                        const { updated_at } = await applicationService.updateStatus(
                                          pairedApp.id,
                                          pipelineForScheduledRound(ev.round),
                                          `Interview round ${ev.round} scheduled`,
                                          undefined,
                                          undefined,
                                          { expectedUpdatedAt: pairedApp.updated_at },
                                        );
                                        setPairedApp({
                                          ...pairedApp,
                                          status: pipelineForScheduledRound(ev.round),
                                          updated_at,
                                        });
                                        setInterviewEvents((prev) =>
                                          sortInterviewEventsDesc(
                                            prev.map((e) =>
                                              e.id === ev.id
                                                ? {
                                                    ...e,
                                                    scheduled_at: iso,
                                                    status: 'scheduled',
                                                    location: confirmLocation.trim(),
                                                    duration_minutes: Number.isFinite(dur) ? dur : 60,
                                                  }
                                                : e
                                            )
                                          )
                                        );
                                        setConfirmForId(null);
                                      } catch (e) {
                                        setLcErr(
                                          isApplicationStatusConflictError(e)
                                            ? e.message
                                            : e instanceof Error
                                              ? e.message
                                              : 'Could not confirm slot',
                                        );
                                      } finally {
                                        setLcBusyId(null);
                                      }
                                    }}
                                  >
                                    [Save schedule & sync pipeline]
                                  </button>
                                </div>
                              )}

                              {ev.status === 'scheduled' && (
                                <div className="mt-2 flex flex-wrap gap-2 border-t vscode-border pt-3">
                                  {cand?.email && (
                                    <Link
                                      to={composeBase}
                                      state={{
                                        returnTo: interviewsReturnPath,
                                        recipientOverride: {
                                          email: cand.email,
                                          name: cand.full_name,
                                        },
                                        templateSelect: 'b:post_interview',
                                        initialCustomBody: `<p style="color:#e4e4e7;font-size:14px;">Thank you for interviewing for <strong>${pairedApp.job?.title ?? job?.title ?? 'the role'}</strong>. Edit this note before sending.</p>`,
                                      }}
                                      className="btn-secondary ggj-text-meta py-1.5 inline-flex items-center gap-1"
                                    >
                                      <Mail className="w-3.5 h-3.5" /> [Thank candidate]
                                    </Link>
                                  )}
                                  <button
                                    type="button"
                                    className="ggj-text-meta px-2 py-1.5 rounded bg-green-900/30 text-green-300"
                                    disabled={busy}
                                    onClick={async () => {
                                      if (!pairedApp) return;
                                      setLcBusyId(ev.id);
                                      try {
                                        await interviewEventService.update(ev.id, { status: 'conducted' });
                                        const { updated_at } = await applicationService.updateStatus(
                                          pairedApp.id,
                                          pipelineForConductedRound(ev.round),
                                          `Interview round ${ev.round} completed`,
                                          undefined,
                                          undefined,
                                          { expectedUpdatedAt: pairedApp.updated_at },
                                        );
                                        setPairedApp({
                                          ...pairedApp,
                                          status: pipelineForConductedRound(ev.round),
                                          updated_at,
                                        });
                                        setInterviewEvents((prev) =>
                                          prev.map((e) =>
                                            e.id === ev.id ? { ...e, status: 'conducted' } : e
                                          )
                                        );
                                      } catch (e) {
                                        setLcErr(
                                          isApplicationStatusConflictError(e)
                                            ? e.message
                                            : e instanceof Error
                                              ? e.message
                                              : 'Could not update pipeline',
                                        );
                                      } finally {
                                        setLcBusyId(null);
                                      }
                                    }}
                                  >
                                    [Mark conducted]
                                  </button>
                                  <button
                                    type="button"
                                    className="ggj-text-meta px-2 py-1.5 rounded bg-red-900/25 text-red-300"
                                    disabled={busy}
                                    onClick={async () => {
                                      if (!pairedApp) return;
                                      setLcBusyId(ev.id);
                                      try {
                                        await interviewEventService.update(ev.id, { status: 'no_show' });
                                        const { updated_at } = await applicationService.updateStatus(
                                          pairedApp.id,
                                          'no_show',
                                          `No-show — interview round ${ev.round}`,
                                          undefined,
                                          undefined,
                                          { expectedUpdatedAt: pairedApp.updated_at },
                                        );
                                        setPairedApp({ ...pairedApp, status: 'no_show', updated_at });
                                        setInterviewEvents((prev) =>
                                          prev.map((e) =>
                                            e.id === ev.id ? { ...e, status: 'no_show' } : e
                                          )
                                        );
                                      } catch (e) {
                                        setLcErr(
                                          isApplicationStatusConflictError(e)
                                            ? e.message
                                            : e instanceof Error
                                              ? e.message
                                              : 'Could not update pipeline',
                                        );
                                      } finally {
                                        setLcBusyId(null);
                                      }
                                    }}
                                  >
                                    [Mark no-show]
                                  </button>
                                  <button
                                    type="button"
                                    className="ggj-text-meta px-2 py-1.5 rounded bg-gray-800 vscode-text-secondary"
                                    disabled={busy}
                                    onClick={async () => {
                                      setLcBusyId(ev.id);
                                      try {
                                        await interviewEventService.update(ev.id, { status: 'cancelled' });
                                        setInterviewEvents((prev) =>
                                          prev.map((e) =>
                                            e.id === ev.id ? { ...e, status: 'cancelled' } : e
                                          )
                                        );
                                      } finally {
                                        setLcBusyId(null);
                                      }
                                    }}
                                  >
                                    [Cancel event]
                                  </button>
                                </div>
                              )}

                              {busy && (
                                <p className="ggj-text-meta vscode-text-tertiary mt-2 flex items-center gap-1">
                                  <Loader2 className="w-3 h-3 animate-spin" /> Updating…
                                </p>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </>
                )}
              </div>
            )}

            {pairedApp && (
              <div className="card">
                <h2 className="ggj-text-body font-semibold vscode-text-primary mb-3 flex items-center gap-2">
                  <ClipboardList className="w-4 h-4 text-brand-400" /> Interview scorecards (ACT-010)
                </h2>
                {detailLoading ? (
                  <p className="ggj-text-meta vscode-text-tertiary">Loading scorecards…</p>
                ) : scorecards.length === 0 ? (
                  <p className="ggj-text-meta vscode-text-tertiary mb-4">No scorecards submitted yet for this application.</p>
                ) : (
                  <ul className="space-y-2 mb-4">
                    {scorecards.map((sc) => (
                      <li
                        key={sc.id}
                        className="ggj-text-meta border vscode-border rounded-[10px] p-3 bg-gray-900/30 vscode-text-secondary"
                      >
                        <div className="flex flex-wrap gap-2 items-baseline">
                          <span className="font-medium vscode-text-primary">Score {sc.overall_score ?? '—'}/5</span>
                          <span className="vscode-text-tertiary">
                            {sc.recommendation ? RECOMMENDATION_LABELS[sc.recommendation] : '—'}
                          </span>
                          <span className="vscode-text-tertiary">· {sc.round}</span>
                        </div>
                        <div className="vscode-text-tertiary mt-1">
                          {sc.interviewer_name ?? 'Unknown interviewer'} ·{' '}
                          {formatDateTimeGmt(sc.submitted_at)}
                        </div>
                        {sc.notes && <p className="vscode-text-secondary mt-2 whitespace-pre-wrap">{sc.notes}</p>}
                      </li>
                    ))}
                  </ul>
                )}

                <div className="border-t vscode-border pt-4 space-y-3">
                  <p className="ggj-text-meta font-medium vscode-text-secondary">Submit scorecard</p>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block ggj-text-meta vscode-text-tertiary mb-1">Link to interview (optional)</label>
                      <select
                        className="input w-full ggj-text-body"
                        value={formEventId}
                        onChange={(e) => onEventPick(e.target.value)}
                      >
                        <option value="">Ad-hoc / not scheduled</option>
                        {interviewEvents.map((ev) => (
                          <option key={ev.id} value={ev.id}>
                            Round {ev.round} — {formatEvWhen(ev)}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block ggj-text-meta vscode-text-tertiary mb-1">Round label</label>
                      <input
                        className="input w-full ggj-text-body"
                        value={formRound}
                        onChange={(e) => setFormRound(e.target.value)}
                        placeholder="e.g. 1 or technical"
                      />
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block ggj-text-meta vscode-text-tertiary mb-1">Overall (1–5)</label>
                      <select
                        className="input w-full ggj-text-body"
                        value={formOverall}
                        onChange={(e) => setFormOverall(Number(e.target.value))}
                      >
                        {[1, 2, 3, 4, 5].map((n) => (
                          <option key={n} value={n}>
                            {n}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block ggj-text-meta vscode-text-tertiary mb-1">Recommendation</label>
                      <select
                        className="input w-full ggj-text-body"
                        value={formRec}
                        onChange={(e) => setFormRec(e.target.value as ScorecardRecommendation)}
                      >
                        {(Object.keys(RECOMMENDATION_LABELS) as ScorecardRecommendation[]).map((k) => (
                          <option key={k} value={k}>
                            {RECOMMENDATION_LABELS[k]}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block ggj-text-meta vscode-text-tertiary mb-2">Rubric (optional)</label>
                    <div className="space-y-2">
                      {rubricRows.map((row, i) => (
                        <div key={i} className="flex gap-2 items-center">
                          <input
                            className="input flex-1 ggj-text-body"
                            placeholder="Criterion"
                            value={row.criterion}
                            onChange={(e) => {
                              const next = [...rubricRows];
                              next[i] = { ...next[i], criterion: e.target.value };
                              setRubricRows(next);
                            }}
                          />
                          <select
                            className="input w-20 ggj-text-body shrink-0"
                            value={row.score}
                            onChange={(e) => {
                              const next = [...rubricRows];
                              next[i] = { ...next[i], score: Number(e.target.value) };
                              setRubricRows(next);
                            }}
                          >
                            {[1, 2, 3, 4, 5].map((n) => (
                              <option key={n} value={n}>
                                {n}
                              </option>
                            ))}
                          </select>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="block ggj-text-meta vscode-text-tertiary mb-1">Notes</label>
                    <textarea
                      className="input w-full ggj-text-body min-h-[80px]"
                      value={formNotes}
                      onChange={(e) => setFormNotes(e.target.value)}
                      placeholder="Strengths, concerns, next steps…"
                    />
                  </div>
                  {saveError && <p className="ggj-text-meta text-red-400">{saveError}</p>}
                  <button
                    type="button"
                    className="btn-primary ggj-text-body"
                    disabled={saving || detailLoading}
                    onClick={() => void submitScorecard()}
                  >
                    {saving ? 'Saving…' : 'Save scorecard'}
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="space-y-4">
            <div className="card">
              <h2 className="ggj-text-body font-semibold vscode-text-primary mb-3 flex items-center gap-2">
                <BookOpen className="w-4 h-4 text-brand-400" /> Suggested Courses
              </h2>
              <p className="ggj-text-meta vscode-text-tertiary mb-2">Based on skill gaps — integrate with learning platforms</p>
              <ul className="space-y-1 ggj-text-body vscode-text-secondary">
                {missingSkills.slice(0, 3).map((s) => (
                  <li key={s}>• Search &quot;{s} course&quot; on Coursera, Udemy, or LinkedIn Learning</li>
                ))}
                {missingSkills.length === 0 && selectedJob && <li>No gaps — candidate is well-prepared</li>}
              </ul>
            </div>
            <div className="card">
              <h2 className="ggj-text-body font-semibold vscode-text-primary mb-3 flex items-center gap-2">
                <Youtube className="w-4 h-4 text-red-500" /> Video Playlists
              </h2>
              <p className="ggj-text-meta vscode-text-tertiary mb-2">Interview prep — YouTube search suggestions</p>
              <ul className="space-y-1 ggj-text-body vscode-text-secondary">
                {job && <li>• &quot;{job.title} interview questions&quot;</li>}
                <li>• &quot;Technical interview tips&quot;</li>
                <li>• &quot;Behavioral interview preparation&quot;</li>
              </ul>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
